import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { EcommerceBannerPlacement, EcommerceResponse, IEcommerceQuestionSchema } from '../../types'
import AssetsGate from '../../components/AssetsGate.tsx'
import { drawnCards } from '../../utils/hiddenClass.ts'
import Filters from './Filters'
import SponsoredCarousel from './SponsoredCarousel'
import BannerAd from './BannerAd'
import Card from './Card'
import Spinner from './Spinner'
import style from './Main.module.scss'

// Fixed number of full rows per page; page size = products_per_row * ROWS_PER_PAGE.
const ROWS_PER_PAGE = 3

interface IMainProps {
  schema: IEcommerceQuestionSchema
  findability: boolean
  page: number
  response: EcommerceResponse
  onPageChange: (next: number) => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt?: number) => void
  onSetQuantity: (index: number, qnt: number) => void
  registerCardRef: (id: string, element: HTMLDivElement | null) => void
  scrollTop: () => void
}

const Main = ({
  schema,
  findability,
  page,
  response,
  onPageChange,
  onItemSelect,
  onAddToCart,
  onSetQuantity,
  registerCardRef,
  scrollTop
}: IMainProps) => {
  // Banner products are pulled into BannerAd; variants/na are never shown in the grid.
  const visibleAnswers = React.useMemo(
    () => schema.answers.filter(answer => !answer.parent_product && !answer.na && !answer.banner),
    [schema.answers]
  )

  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const cols = schema.products_per_row || 4
  const pageSize = cols * ROWS_PER_PAGE
  const totalPages = Math.max(1, Math.ceil(visibleAnswers.length / pageSize))
  const safePage = Math.min(page, totalPages)
  // Mobile: single column, all products on one page (no pagination, no filters sidebar).
  const items = viewport.mobile ? visibleAnswers : visibleAnswers.slice((safePage - 1) * pageSize, safePage * pageSize)
  // A card's carousel opens on the product's main image. The banner above waits for its own pictures with
  // its own spinner, so the two load side by side.
  const cardImages = drawnCards(items).map(answer => answer.image && getMediumAssetPath(answer.image))

  const goTo = (next: number) => {
    if (next < 1 || next > totalPages || next === safePage) return
    onPageChange(next)
    scrollTop()
  }

  return (
    <section className={`${style.main} ${viewport.mobile ? style.mobile : ''}`}>
      <SponsoredCarousel
        schema={schema}
        findability={findability}
        response={response}
        onItemSelect={onItemSelect}
        onAddToCart={onAddToCart}
        onSetQuantity={onSetQuantity}
      />

      <div className={style.body}>
        {!viewport.mobile && <Filters />}
        <div className={style.content}>
          {<BannerAd schema={schema} onItemSelect={onItemSelect} />}

          <div className={style.sortRow}>
            {viewport.mobile ? (
              <>
                <EmptyLink color="#1C49C4">
                  <div className={style.sortFilterBtn}>
                    <i className="pi pi-sort-alt" />
                    <span>{t('questions', 'ec', 'chewy', 'sort')}</span>
                  </div>
                </EmptyLink>
                <EmptyLink color="#1C49C4">
                  <div className={style.sortFilterBtn}>
                    <i className="pi pi-filter" />
                    <span>{t('questions', 'ec', 'chewy', 'filter')}</span>
                  </div>
                </EmptyLink>
              </>
            ) : (
              <EmptyLink color="#1C49C4">
                <fieldset className={style.sortSelect}>
                  <legend className={style.sortLegend}>{t('questions', 'ec', 'chewy', 'sortBy')}</legend>
                  <span className={style.sortValue}>{t('questions', 'ec', 'chewy', 'relevance')}</span>
                  <i className={`pi pi-chevron-down ${style.sortChevron}`} />
                </fieldset>
              </EmptyLink>
            )}
          </div>

          <AssetsGate urls={cardImages} loader={<Spinner />}>
            <div className={style.grid} style={{ ['--cols' as string]: cols }}>
              {items.map(answer => (
                <Card
                  key={answer.id}
                  ref={element => registerCardRef(answer.id, element)}
                  data-testid="SW-EC-Item"
                  schema={schema}
                  answer={answer}
                  findability={findability}
                  amount={response.answers[answer.index].v}
                  disabled={'focus' == schema.mode_type && !answer.focused}
                  onItemSelect={onItemSelect}
                  onAddToCart={onAddToCart}
                  onSetQuantity={onSetQuantity}
                />
              ))}
            </div>
          </AssetsGate>

          {!viewport.mobile && totalPages > 1 && (
            <nav className={style.pagination} aria-label={t('questions', 'ec', 'chewy', 'pagination')}>
              <button
                type="button"
                className={style.paginationLink}
                onClick={() => goTo(safePage - 1)}
                disabled={safePage === 1}
              >
                <i className="pi pi-chevron-left" />
                {t('questions', 'ec', 'chewy', 'previous')}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  type="button"
                  className={`${style.paginationPage} ${p === safePage ? style.paginationPageActive : ''}`}
                  onClick={() => goTo(p)}
                  aria-current={p === safePage ? 'page' : undefined}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                className={style.paginationLink}
                onClick={() => goTo(safePage + 1)}
                disabled={safePage === totalPages}
              >
                {t('questions', 'ec', 'chewy', 'next')}
                <i className="pi pi-chevron-right" />
              </button>
            </nav>
          )}
        </div>
      </div>
    </section>
  )
}

export default Main
